import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

import { withDevtools } from './middleware-utils';

export interface CatalogOption {
  value: string;
  label: string;
  category?: string;
  description?: string;
  riskLevel?: string;
  /** Código del sector SCIAN al que pertenece (scian_classes / scian_subsectors). */
  sector?: string;
  /** Código del subsector SCIAN al que pertenece (scian_classes). */
  subsector?: string;
}

interface RawCatalog {
  searchable: boolean;
  grouped?: boolean;
  group_by?: string;
  items: {
    /**
     * Los catálogos PM (`pm_funds_origins`, `pm_operation_amount_ranges`, …)
     * entregan el código como ENTERO, no como string: `{ "code": 3, "label":
     * "Arrendamiento…" }`. Un `<select>` sólo produce strings, así que un
     * `value` numérico no volvía a casar nunca con la opción elegida.
     */
    code: string | number;
    label: string;
    category?: string;
    description?: string;
    risk_level?: string;
    sector?: string;
    subsector?: string;
  }[];
}

interface CatalogsState {
  catalogs: Record<string, CatalogOption[]>;
  requestedKeys: string[];
  isLoaded: boolean;
  contentHashes: Record<string, string>;
  contractContents: Record<string, string>;

  // Actions
  setCatalogs: (rawCatalogs: Record<string, RawCatalog>, requestedKeys: string[]) => void;
  getCatalog: (key: string) => CatalogOption[];
  setContentHash: (contractType: string, hash: string) => void;
  getContentHash: (contractType: string) => string | undefined;
  setContractContent: (contractType: string, html: string) => void;
  getContractContent: (contractType: string) => string | undefined;
  reset: () => void;
}

/**
 * Referencia estable para los catálogos que aún no llegaron. Devolver `[]`
 * literal desde el selector crearía un array nuevo en cada render y zustand lo
 * leería como cambio de estado: re-render infinito.
 */
const EMPTY_CATALOG: CatalogOption[] = [];

export const useCatalogsStore = create<CatalogsState>()(
  withDevtools(
    'Catalogs',
    persist(
      (set, get) => ({
        catalogs: {},
        requestedKeys: [],
        isLoaded: false,
        contentHashes: {},
        contractContents: {},

        setCatalogs: (rawCatalogs, requestedKeys) => {
          const catalogs: Record<string, CatalogOption[]> = {};
          for (const [key, catalog] of Object.entries(rawCatalogs)) {
            catalogs[key] = catalog.items.map(item => ({
              // A string SIEMPRE: ver la nota del `code` arriba. Para los
              // catálogos que ya lo entregaban como texto esto no cambia nada.
              value: String(item.code),
              label: item.label,
              category: item.category,
              description: item.description,
              riskLevel: item.risk_level,
              sector: item.sector,
              subsector: item.subsector,
            }));
          }
          set(state => ({
            catalogs: { ...state.catalogs, ...catalogs },
            requestedKeys: [...new Set([...state.requestedKeys, ...requestedKeys])],
            isLoaded: true,
          }));
        },

        getCatalog: key => {
          return get().catalogs[key] ?? [];
        },

        setContentHash: (contractType, hash) => {
          set(state => ({
            contentHashes: { ...state.contentHashes, [contractType]: hash },
          }));
        },

        getContentHash: contractType => {
          return get().contentHashes[contractType];
        },

        setContractContent: (contractType, html) => {
          set(state => ({
            contractContents: { ...state.contractContents, [contractType]: html },
          }));
        },

        getContractContent: contractType => {
          return get().contractContents[contractType];
        },

        reset: () => {
          set({
            catalogs: {},
            requestedKeys: [],
            isLoaded: false,
            contentHashes: {},
            contractContents: {},
          });
        },
      }),
      {
        name: 'catalogs-storage',
        storage: createJSONStorage(() => sessionStorage),
        partialize: state => ({
          catalogs: state.catalogs,
          requestedKeys: state.requestedKeys,
          isLoaded: state.isLoaded,
          contentHashes: state.contentHashes,
          contractContents: state.contractContents,
        }),
      }
    )
  )
);

/**
 * Opciones de un catálogo, **suscritas al store**.
 *
 * Es la forma correcta de leerlo desde un componente. `getCatalog` resuelve con
 * `get()`, así que seleccionarlo (`useCatalogsStore(s => s.getCatalog)`) sólo
 * suscribe a la identidad de esa función —que nunca cambia—: cuando la query
 * llena el store DESPUÉS del primer paint, el componente no se entera. Se veía
 * en el perfil transaccional PM, que abría con los cinco `<select>`
 * deshabilitados hasta que cualquier otra cosa forzaba un render y entonces las
 * opciones aparecían solas.
 *
 * `getCatalog` sigue siendo válido fuera de render (handlers, efectos), donde
 * lo que se quiere es justamente el valor del momento sin suscribirse.
 */
export function useCatalog(key: string): CatalogOption[] {
  return useCatalogsStore(state => state.catalogs[key] ?? EMPTY_CATALOG);
}
